/**
 * Peças puras da conversa persistida — sem DOM, sem Office.js, sem rede.
 *
 * A restauração mexe em duas coisas caras de errar (o documento do usuário e o
 * histórico que vai ao modelo), então tudo que dá para decidir sem tocar no
 * host mora aqui e é testável isoladamente (ver scripts/check-session.mjs).
 */
/**
 * A bolha que abriu um turno interrompido, se houver. Só a ÚLTIMA conta: um
 * turno novo sempre começa depois de o anterior ter sido encerrado.
 */
export function interruptedTurnEntry(transcript) {
    for (let i = transcript.length - 1; i >= 0; i--) {
        if (transcript[i].pending)
            return transcript[i];
    }
    return null;
}
/** Teto do título salvo — o backend também limita (`max_length=200`). */
export const TITLE_MAX_CHARS = 80;
/**
 * Título da conversa a partir da primeira coisa que o usuário pediu.
 *
 * Só a primeira LINHA: pedidos costumam vir com contexto colado depois de uma
 * quebra, e um título de três parágrafos não identifica nada numa lista de
 * cinco. Sem primeira mensagem de usuário, um rótulo neutro — nunca vazio, que
 * na lista viraria um card sem nome.
 */
export function deriveTitle(transcript, fallback = 'Conversa') {
    const primeiro = transcript.find((e) => e.role === 'user' && e.text.trim());
    if (!primeiro)
        return fallback;
    const linha = primeiro.text.trim().split(/\r?\n/, 1)[0].trim();
    if (!linha)
        return fallback;
    if (linha.length <= TITLE_MAX_CHARS)
        return linha;
    // Corta na última palavra inteira que couber, para não terminar no meio de uma.
    const cortado = linha.slice(0, TITLE_MAX_CHARS);
    const ultimoEspaco = cortado.lastIndexOf(' ');
    const base = ultimoEspaco > 40 ? cortado.slice(0, ultimoEspaco) : cortado;
    // `replace` e não `trimEnd`: o alvo do tsconfig é anterior ao es2019.
    return `${base.replace(/\s+$/, '')}…`;
}
/**
 * Primeiro nome, para a saudação da tela de retomada. `null` = não dá para
 * saudar (e aí a tela usa o título neutro em vez de "Olá, .").
 *
 * A claim `name` do diretório corporativo não é um "Nome Sobrenome" confiável:
 *  - muitos diretórios gravam sobrenome primeiro ("Pedrosa, Samuel") — pegar o
 *    primeiro token daria "Pedrosa," com vírgula e tudo;
 *  - AD com cadastro em caixa alta é comum, e "Olá, SAMUEL." parece grito.
 */
export function firstName(name) {
    var _a;
    const limpo = (name !== null && name !== void 0 ? name : '').trim();
    if (!limpo)
        return null;
    // "Sobrenome, Nome" -> o que interessa está DEPOIS da vírgula.
    const base = limpo.includes(',') ? limpo.slice(limpo.indexOf(',') + 1) : limpo;
    const token = (_a = base.trim().split(/\s+/)[0]) !== null && _a !== void 0 ? _a : '';
    if (!token)
        return null;
    // Caixa alta vira capitalizado; qualquer outra grafia fica como o dono escreveu
    // (nomes com maiúscula no meio, como "McCarthy", são legítimos).
    const tudoMaiusculo = token === token.toUpperCase() && token !== token.toLowerCase();
    return tudoMaiusculo
        ? token.charAt(0) + token.slice(1).toLowerCase()
        : token;
}
/**
 * "agora", "há 3 h", "ontem", "12/03" — a data do card da lista.
 *
 * Prioriza o recente porque é o que distingue as opções: cinco conversas da
 * mesma semana, com a mesma cara, escolhidas pela mais nova. Datas antigas viram
 * dia/mês, que já basta para reconhecer.
 */
export function formatWhen(iso, agora = new Date()) {
    const quando = new Date(iso);
    if (Number.isNaN(quando.getTime()))
        return '';
    const minutos = Math.floor((agora.getTime() - quando.getTime()) / 60000);
    if (minutos < 1)
        return 'agora';
    if (minutos < 60)
        return `há ${minutos} min`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24)
        return `há ${horas} h`;
    const dias = Math.floor(horas / 24);
    if (dias === 1)
        return 'ontem';
    if (dias < 7)
        return `há ${dias} dias`;
    return quando.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}
/** "12 mensagens · Proposta Técnica · há 3 h" — a segunda linha do card. */
export function summarizeConversation(info, agora = new Date()) {
    const partes = [];
    if (info.message_count > 0) {
        partes.push(`${info.message_count} ${info.message_count === 1 ? 'mensagem' : 'mensagens'}`);
    }
    if (info.document_type_name)
        partes.push(info.document_type_name);
    const quando = formatWhen(info.updated_at, agora);
    if (quando)
        partes.push(quando);
    return partes.join(' · ');
}
/**
 * O histórico salvo pode ser restaurado direto no `messages` do agent loop?
 *
 * A API da Anthropic exige alternância de papéis e um `tool_result` para cada
 * `tool_use`. Um histórico gravado no meio de um turno interrompido (aba
 * fechada durante a escrita, por exemplo) viola isso, e o erro só apareceria no
 * PRIMEIRO envio depois de restaurar — quando o usuário já digitou o pedido e
 * já não associa a falha à restauração. Preferimos voltar só a transcrição
 * visível: a conversa continua na tela, o modelo recomeça sem memória.
 */
export function historyIsResumable(messages) {
    if (messages.length === 0)
        return true;
    const ultima = messages[messages.length - 1];
    if (ultima.role !== 'assistant')
        return false;
    const pendentes = new Set();
    for (const m of messages) {
        const blocos = Array.isArray(m.content) ? m.content : [];
        for (const b of blocos) {
            if (b.type === 'tool_use' && typeof b.id === 'string')
                pendentes.add(b.id);
            if (b.type === 'tool_result' && typeof b.tool_use_id === 'string') {
                pendentes.delete(b.tool_use_id);
            }
        }
    }
    return pendentes.size === 0;
}
